import React, { useEffect, useState, useMemo, useCallback } from "react";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    Button,
    MenuItem,
    FormControl,
    Select,
    InputLabel,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Grid,
    Chip,
    CircularProgress,
    LinearProgress,
    Tooltip as MuiTooltip,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import EditLocationOutlinedIcon from "@material-ui/icons/EditLocationOutlined";
import AccountBalanceWalletOutlinedIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import LocationCityOutlinedIcon from "@material-ui/icons/LocationCityOutlined";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import TuneIcon from "@material-ui/icons/Tune";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";

const BLUE = "#2a78d6";
const BLUE_DARK = "#1b5fad";
const DEEP = "#0f4c81";
const TEAL = "#0d9488";
const PURPLE = "#7c5cd6";
const GREEN = "#0f9d58";
const HEADER_GRADIENT = `linear-gradient(135deg, ${DEEP} 0%, ${BLUE} 62%, #5ba3ec 100%)`;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const SPEND_FIELDS = [
    { id: "google", label: "Google" },
    { id: "meta", label: "Meta" },
    { id: "others", label: "Others" },
];

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value) => `₹${toNumber(value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const useStyles = makeStyles(() => ({
    pageRoot: {
        background: "linear-gradient(180deg, #F7F9FC 0%, #EFF3F9 100%)",
        minHeight: "100vh",
        padding: "14px 18px 32px",
    },
    banner: {
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
        padding: "20px 26px",
        borderRadius: 18,
        marginBottom: 16,
        background: HEADER_GRADIENT,
        boxShadow: "0 12px 30px rgba(15,76,129,0.26)",
        "&::after": {
            content: '""',
            position: "absolute",
            top: -80,
            right: -30,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
        },
        "& > *": { position: "relative", zIndex: 1 },
    },
    bannerIcon: {
        width: 50,
        height: 50,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: "rgba(255,255,255,0.18)",
        border: "2px solid rgba(255,255,255,0.32)",
        color: "#fff",
    },
    bannerTitle: { fontSize: 20, fontWeight: 700, color: "#fff", letterSpacing: 0.2 },
    bannerSubtitle: { fontSize: 13, color: "rgba(255,255,255,0.8)", marginTop: 4 },
    bannerChip: {
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 16px",
        borderRadius: 20,
        background: "rgba(255,255,255,0.18)",
        color: "#fff",
        fontWeight: 700,
        fontSize: 13,
        whiteSpace: "nowrap",
    },
    refreshButton: {
        color: "#fff",
        backgroundColor: "rgba(255,255,255,0.18)",
        border: "1px solid rgba(255,255,255,0.32)",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
    },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    spinning: { animation: "$spin 0.9s linear infinite" },

    filterCard: {
        padding: "16px 20px",
        marginBottom: 16,
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
    },
    filterTitle: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: BLUE_DARK,
        fontWeight: 700,
        fontSize: 13,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 14,
    },
    filterRow: { display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" },
    field: {
        "& .MuiOutlinedInput-root": { borderRadius: 10 },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
        "& .MuiInputLabel-root.Mui-focused": { color: BLUE_DARK },
    },
    notice: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "11px 16px",
        borderRadius: 11,
        background: "#EAF2FD",
        border: "1px solid #cfe1f8",
        color: "#1b4a7a",
        fontSize: 13,
        fontWeight: 500,
        marginTop: 14,
    },

    statCard: {
        height: "100%",
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 26px rgba(28,55,93,0.12)" },
    },
    statInner: { display: "flex", alignItems: "center", gap: 14, padding: "16px 18px !important" },
    statIcon: {
        width: 46,
        height: 46,
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statLabel: {
        fontSize: 11.5,
        fontWeight: 700,
        color: "#8b93a6",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    statValue: { fontSize: 23, fontWeight: 700, color: "#1f2d3d", marginTop: 3, lineHeight: 1.15 },
    statSub: { fontSize: 11.5, color: "#9aa4b4", marginTop: 3 },

    tableCard: {
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        overflow: "hidden",
    },
    tableToolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        padding: "16px 18px 14px",
    },
    tableTitle: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        fontSize: 15,
        fontWeight: 700,
        color: "#1f2d3d",
        "&::before": {
            content: '""',
            width: 4,
            height: 16,
            borderRadius: 3,
            background: `linear-gradient(180deg, ${BLUE}, ${DEEP})`,
        },
    },
    tableSubtitle: { fontSize: 12, color: "#8b93a6", marginTop: 3, paddingLeft: 13 },
    searchField: {
        "& .MuiOutlinedInput-root": { borderRadius: 999, backgroundColor: "#fff" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
    },
    tableWrap: { maxHeight: 600, overflow: "auto" },
    headCell: {
        background: `linear-gradient(135deg, ${DEEP} 0%, ${BLUE_DARK} 100%)`,
        color: "#EAF2FD",
        fontWeight: 600,
        fontSize: 11.5,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        border: "none",
        padding: "13px 16px",
    },
    bodyCell: {
        borderBottom: "1px solid #edf0f5",
        padding: "10px 16px",
        fontSize: 13.5,
        color: "#41506b",
    },
    bodyRow: {
        transition: "background-color 0.15s ease",
        "&:nth-of-type(even)": { backgroundColor: "#FBFCFE" },
        "&:hover": { backgroundColor: "#F1F6FD" },
    },
    rowDirty: { backgroundColor: "#F2F8FF !important" },
    locationName: { fontWeight: 600, color: "#2b3648" },
    amountInput: {
        width: 130,
        "& .MuiOutlinedInput-root": { borderRadius: 9, backgroundColor: "#fff" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
        "& input": { textAlign: "right", fontWeight: 600, color: "#2b3648" },
    },
    rowTotal: { fontWeight: 700, color: DEEP, whiteSpace: "nowrap" },
    submitBtn: {
        background: `linear-gradient(135deg, ${DEEP}, ${BLUE})`,
        color: "#fff",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 9,
        padding: "6px 18px",
        boxShadow: "0 3px 9px rgba(27,95,173,0.28)",
        "&:hover": { background: `linear-gradient(135deg, #0b3c68, ${BLUE_DARK})` },
        "&.Mui-disabled": { background: "#dde4ee", color: "#9aa4b4", boxShadow: "none" },
    },
    savedChip: {
        backgroundColor: "#E7F6EC",
        color: GREEN,
        fontWeight: 700,
        border: `1px solid ${GREEN}33`,
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "44px 20px",
        color: "#9aa4b4",
        textAlign: "center",
    },
    emptyIcon: { fontSize: 36, color: "#c7cedb", marginBottom: 8 },
}));

const StatCard = ({ label, value, sub, accent, icon, loading, classes }) => (
    <Card className={classes.statCard}>
        <CardContent className={classes.statInner}>
            <div className={classes.statIcon} style={{ backgroundColor: `${accent}1a`, color: accent }}>
                {icon}
            </div>
            <div style={{ minWidth: 0 }}>
                <div className={classes.statLabel}>{label}</div>
                {loading ? (
                    <Skeleton variant="text" width={100} height={30} />
                ) : (
                    <>
                        <div className={classes.statValue}>{value}</div>
                        {sub ? <div className={classes.statSub}>{sub}</div> : null}
                    </>
                )}
            </div>
        </CardContent>
    </Card>
);

const SpendReportValues = (props) => {
    const classes = useStyles();
    const { setSnackInfo } = props;

    const currentYear = new Date().getFullYear();
    const yearList = useMemo(() => Array.from({ length: 4 }, (_, i) => currentYear - i), [currentYear]);

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedYear, setSelectedYear] = useState("");
    const [search, setSearch] = useState("");
    // Which row is mid-save, and which rows were saved for the current period.
    const [submittingId, setSubmittingId] = useState(null);
    const [savedIds, setSavedIds] = useState([]);

    const getLocations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await HttpService.getLocation();
            setLocations(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            setLocations([]);
            setSnackInfo("Failed to fetch locations", "error");
        }
        setLoading(false);
    }, [setSnackInfo]);

    useEffect(() => {
        getLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Switching period invalidates anything typed or saved against the old one.
    const resetEntries = () => {
        setFormData({});
        setSavedIds([]);
    };

    const handleMonthChange = (e) => {
        setSelectedMonth(e.target.value);
        resetEntries();
    };

    const handleYearChange = (e) => {
        setSelectedYear(e.target.value);
        resetEntries();
    };

    const handleChange = (locId, field, value) => {
        const numericValue = Number(String(value).replace(/\D/g, "")) || 0;

        setFormData((prev) => ({
            ...prev,
            [locId]: {
                google: 0,
                meta: 0,
                others: 0,
                ...(prev[locId] || {}),
                [field]: numericValue,
                month: selectedMonth,
                year: selectedYear,
            },
        }));

        // Editing a saved row means it no longer matches what was submitted.
        setSavedIds((prev) => prev.filter((id) => id !== locId));
    };

    const handleSubmit = async (location) => {
        const payload = formData[location._id] || {};
        setSubmittingId(location._id);
        try {
            await HttpService.spendReportValues({
                locationId: location._id,
                month: selectedMonth,
                year: selectedYear,
                google: toNumber(payload.google),
                meta: toNumber(payload.meta),
                others: toNumber(payload.others),
            });

            setSnackInfo(`Spend values submitted for ${location.name}`, "success");
            setFormData((prev) => ({
                ...prev,
                [location._id]: {
                    google: "",
                    meta: "",
                    others: "",
                    month: selectedMonth,
                    year: selectedYear,
                },
            }));
            setSavedIds((prev) => (prev.includes(location._id) ? prev : [...prev, location._id]));
        } catch (error) {
            console.error("Submit Error:", error);
            setSnackInfo(`Failed to submit spend values for ${location.name}`, "error");
        }
        setSubmittingId(null);
    };

    const periodReady = Boolean(selectedMonth && selectedYear);
    const periodLabel = periodReady ? `${selectedMonth} ${selectedYear}` : "No period selected";

    const valuesOf = (locId) => {
        const row = formData[locId] || {};
        return SPEND_FIELDS.map((f) => toNumber(row[f.id]));
    };

    const rowTotal = (locId) => valuesOf(locId).reduce((sum, n) => sum + n, 0);

    const visibleLocations = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return locations;
        return locations.filter((loc) => String(loc.name || "").toLowerCase().includes(term));
    }, [locations, search]);

    const summary = useMemo(() => {
        const filled = locations.filter((loc) => rowTotal(loc._id) > 0);
        const entered = filled.reduce((sum, loc) => sum + rowTotal(loc._id), 0);
        return { filled: filled.length, entered };
        // rowTotal reads formData, so this must recompute whenever it changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations, formData]);

    const renderSkeletonRows = () =>
        Array.from({ length: 6 }).map((_, i) => (
            <TableRow key={`sk-${i}`}>
                <TableCell className={classes.bodyCell} align="center"><Skeleton variant="text" width={22} style={{ margin: "0 auto" }} /></TableCell>
                <TableCell className={classes.bodyCell}><Skeleton variant="text" width="60%" /></TableCell>
                {SPEND_FIELDS.map((f) => (
                    <TableCell key={f.id} className={classes.bodyCell}><Skeleton variant="rect" height={36} style={{ borderRadius: 9 }} /></TableCell>
                ))}
                <TableCell className={classes.bodyCell} align="right"><Skeleton variant="text" width="50%" style={{ marginLeft: "auto" }} /></TableCell>
                <TableCell className={classes.bodyCell}><Skeleton variant="rect" height={32} style={{ borderRadius: 9 }} /></TableCell>
            </TableRow>
        ));

    return (
        <div className={classes.pageRoot}>
            <div className={classes.banner}>
                <div className={classes.bannerIcon}>
                    <EditLocationOutlinedIcon style={{ fontSize: 26 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.bannerTitle}>Spend Entry</div>
                    <div className={classes.bannerSubtitle}>
                        Record Google, Meta and other marketing spend per location for a month.
                    </div>
                </div>
                <div className={classes.bannerChip}>
                    <DateRangeOutlinedIcon style={{ fontSize: 16 }} />
                    {periodLabel}
                </div>
                <MuiTooltip title="Reload locations">
                    <span>
                        <IconButton
                            size="small"
                            className={classes.refreshButton}
                            onClick={getLocations}
                            disabled={loading}
                        >
                            <RefreshIcon className={loading ? classes.spinning : undefined} style={{ fontSize: 20 }} />
                        </IconButton>
                    </span>
                </MuiTooltip>
            </div>

            <Paper className={classes.filterCard}>
                <div className={classes.filterTitle}>
                    <TuneIcon fontSize="small" />
                    Reporting period
                </div>
                <div className={classes.filterRow}>
                    <FormControl variant="outlined" size="small" className={classes.field} style={{ minWidth: 200 }}>
                        <InputLabel>Select Month</InputLabel>
                        <Select
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            label="Select Month"
                            disabled={loading}
                        >
                            {MONTHS.map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className={classes.field} style={{ minWidth: 160 }}>
                        <InputLabel>Select Year</InputLabel>
                        <Select
                            value={selectedYear}
                            onChange={handleYearChange}
                            label="Select Year"
                            disabled={loading}
                        >
                            {yearList.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {periodReady && (
                        <Chip
                            icon={<CheckCircleIcon style={{ color: GREEN }} />}
                            label={`Entering for ${periodLabel}`}
                            style={{ backgroundColor: "#E7F6EC", color: "#0b6b3d", fontWeight: 600 }}
                        />
                    )}
                </div>

                {!periodReady && (
                    <div className={classes.notice}>
                        <InfoOutlinedIcon style={{ fontSize: 18 }} />
                        Select both a month and a year to unlock the spend fields below.
                    </div>
                )}
            </Paper>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Locations"
                        value={locations.length}
                        sub={search ? `${visibleLocations.length} matching search` : "available to update"}
                        accent={BLUE}
                        icon={<LocationCityOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Rows Filled"
                        value={summary.filled}
                        sub="with a value typed in"
                        accent={PURPLE}
                        icon={<PlaylistAddCheckIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Total Entered"
                        value={formatMoney(summary.entered)}
                        sub="not yet submitted"
                        accent={TEAL}
                        icon={<AccountBalanceWalletOutlinedIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        classes={classes}
                        loading={loading}
                        label="Saved"
                        value={savedIds.length}
                        sub={periodReady ? `this session · ${periodLabel}` : "no period selected"}
                        accent={GREEN}
                        icon={<CheckCircleIcon />}
                    />
                </Grid>
            </Grid>

            <Card className={classes.tableCard}>
                {loading && <LinearProgress />}
                <div className={classes.tableToolbar}>
                    <div>
                        <div className={classes.tableTitle}>Location-wise spend</div>
                        <div className={classes.tableSubtitle}>
                            {loading
                                ? "Loading locations…"
                                : `${visibleLocations.length} ${visibleLocations.length === 1 ? "location" : "locations"} shown · submit each row after entering its values`}
                        </div>
                    </div>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search location…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={classes.searchField}
                        style={{ minWidth: 240 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ fontSize: 18, color: "#9aa4b4" }} />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")} style={{ padding: 4 }}>
                                        <ClearIcon style={{ fontSize: 15 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>

                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" className={classes.headCell} style={{ width: 70 }}>Sl. No.</TableCell>
                                <TableCell className={classes.headCell}>Location</TableCell>
                                {SPEND_FIELDS.map((f) => (
                                    <TableCell key={f.id} className={classes.headCell}>{f.label}</TableCell>
                                ))}
                                <TableCell align="right" className={classes.headCell}>Row Total</TableCell>
                                <TableCell className={classes.headCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                renderSkeletonRows()
                            ) : visibleLocations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} style={{ padding: 0, borderBottom: "none" }}>
                                        <div className={classes.emptyState}>
                                            <InboxOutlinedIcon className={classes.emptyIcon} />
                                            <div style={{ fontSize: 14, fontWeight: 600, color: "#6b7689" }}>
                                                {search ? "No locations match your search" : "No locations found"}
                                            </div>
                                            <div style={{ fontSize: 12.5, marginTop: 4 }}>
                                                {search
                                                    ? "Try a different name or clear the search."
                                                    : "Reload the page or check the locations master."}
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleLocations.map((loc, index) => {
                                    const row = formData[loc._id] || {};
                                    const total = rowTotal(loc._id);
                                    const isSaved = savedIds.includes(loc._id);
                                    const isSubmitting = submittingId === loc._id;
                                    const canSubmit = periodReady && total > 0 && !isSubmitting;

                                    return (
                                        <TableRow
                                            key={loc._id}
                                            className={`${classes.bodyRow} ${total > 0 ? classes.rowDirty : ""}`}
                                        >
                                            <TableCell className={classes.bodyCell} align="center" style={{ color: "#9aa4b4" }}>
                                                {index + 1}
                                            </TableCell>
                                            <TableCell className={`${classes.bodyCell} ${classes.locationName}`}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    {loc.name}
                                                    {isSaved && (
                                                        <Chip
                                                            size="small"
                                                            label="Saved"
                                                            className={classes.savedChip}
                                                            icon={<CheckCircleIcon style={{ fontSize: 14, color: GREEN }} />}
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>

                                            {SPEND_FIELDS.map((f) => (
                                                <TableCell key={f.id} className={classes.bodyCell}>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        className={classes.amountInput}
                                                        disabled={!periodReady || isSubmitting}
                                                        placeholder="0"
                                                        value={row[f.id] || ""}
                                                        onChange={(e) => handleChange(loc._id, f.id, e.target.value)}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <span style={{ color: "#9aa4b4", fontSize: 13 }}>₹</span>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        inputProps={{ inputMode: "numeric" }}
                                                    />
                                                </TableCell>
                                            ))}

                                            <TableCell className={`${classes.bodyCell} ${classes.rowTotal}`} align="right">
                                                {total > 0 ? formatMoney(total) : <span style={{ color: "#b6bdc9" }}>—</span>}
                                            </TableCell>

                                            <TableCell className={classes.bodyCell}>
                                                <MuiTooltip
                                                    title={
                                                        !periodReady
                                                            ? "Select a month and year first"
                                                            : total === 0
                                                                ? "Enter at least one spend value"
                                                                : `Submit ${loc.name} for ${periodLabel}`
                                                    }
                                                >
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            className={classes.submitBtn}
                                                            disabled={!canSubmit}
                                                            onClick={() => handleSubmit(loc)}
                                                            startIcon={
                                                                isSubmitting ? (
                                                                    <CircularProgress size={14} color="inherit" />
                                                                ) : (
                                                                    <SaveOutlinedIcon style={{ fontSize: 16 }} />
                                                                )
                                                            }
                                                        >
                                                            {isSubmitting ? "Saving…" : "Submit"}
                                                        </Button>
                                                    </span>
                                                </MuiTooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </div>
    );
};

export default connect(null, { setSnackInfo })(SpendReportValues);
